/**
 * GENERATED_ASSET — hand-written validator mirroring fountain.recipe.schema.json.
 * checks/validate.test.ts runs the same inputs through ajv + the schema file (when ajv is readable)
 * to prove parity. One addition beyond the schema: the id's variant segment must equal params.variant.
 */
import type { FountainRecipe } from './types';
import { isPiecePaletteId } from '../../../../../shared/assets/pieces/palettes';

export interface Issue { path: string; message: string }
export class RecipeValidationError extends Error {
  constructor(readonly issues: readonly Issue[]) {
    super(`Invalid tidal cascade fountain recipe (${issues.length} problem${issues.length === 1 ? '' : 's'}):\n` +
      issues.map((i) => ` - ${i.path || '(root)'}: ${i.message}`).join('\n'));
    this.name = 'RecipeValidationError';
  }
}

const ROOT_KEYS = ['schema', 'id', 'assetId', 'generatorId', 'seed', 'paletteId', 'detail', 'params'];
const PARAM_KEYS = ['variant', 'width', 'height', 'tiers', 'flow', 'waterLevel', 'mossCoverage', 'stoneWear'];
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const show = (v: unknown) => (typeof v === 'string' ? JSON.stringify(v) : String(v));

export function collectIssues(input: unknown): Issue[] {
  const out: Issue[] = [];
  const bad = (path: string, message: string) => out.push({ path, message });
  if (!isObj(input)) { bad('', 'recipe must be an object'); return out; }
  for (const k of Object.keys(input)) if (!ROOT_KEYS.includes(k)) bad(k, 'unknown property (the schema forbids additional properties)');
  for (const k of ROOT_KEYS) if (!(k in input)) bad(k, 'required property is missing');
  const eq = (path: string, v: unknown, want: string | number) => {
    if (path in input && v !== want) bad(path, `must be exactly ${show(want)} (got ${show(v)})`);
  };
  eq('schema', input.schema, 'hush.asset-recipe.v1');
  eq('assetId', input.assetId, 'prop.coastal.tidal_cascade_fountain');
  eq('generatorId', input.generatorId, 'scenery.tidal_cascade_fountain.v1');
  if ('id' in input && (typeof input.id !== 'string' || !/^coastal\.tidal_cascade_fountain\.[a-z_]+\.v1$/.test(input.id))) {
    bad('id', `must match coastal.tidal_cascade_fountain.<variant>.v1 (got ${show(input.id)})`);
  }
  if ('seed' in input && !(typeof input.seed === 'number' && Number.isInteger(input.seed) && input.seed >= 0 && input.seed <= 4294967295)) {
    bad('seed', `must be an integer in [0, 4294967295] (got ${show(input.seed)})`);
  }
  if ('paletteId' in input && !isPiecePaletteId(input.paletteId)) {
    bad('paletteId', `must be a registered piece palette (got ${show(input.paletteId)})`);
  }
  if ('detail' in input && !['readable', 'standard', 'close'].includes(input.detail as string)) {
    bad('detail', `must be one of readable, standard, close (got ${show(input.detail)})`);
  }
  const p = input.params;
  if ('params' in input) {
    if (!isObj(p)) bad('params', 'must be an object');
    else {
      for (const k of Object.keys(p)) if (!PARAM_KEYS.includes(k)) bad(`params.${k}`, 'unknown property (the schema forbids additional properties)');
      for (const k of PARAM_KEYS) if (!(k in p)) bad(`params.${k}`, 'required property is missing');
      if ('variant' in p && !['harbor', 'mossgrown', 'dry_ruin'].includes(p.variant as string)) {
        bad('params.variant', `must be one of harbor, mossgrown, dry_ruin (got ${show(p.variant)})`);
      }
      if ('width' in p && p.width !== 384) bad('params.width', `must be exactly 384, the design frame width (got ${show(p.width)})`);
      if ('height' in p && p.height !== 320) bad('params.height', `must be exactly 320, the design frame height (got ${show(p.height)})`);
      if ('tiers' in p && p.tiers !== 2 && p.tiers !== 3) bad('params.tiers', `must be 2 or 3 (got ${show(p.tiers)})`);
      const range = (k: string, max: number) => {
        if (!(k in p)) return;
        const v = p[k];
        if (typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > max) bad(`params.${k}`, `must be a finite number in [0, ${max}] (got ${show(v)})`);
      };
      range('flow', 1); range('waterLevel', 1); range('mossCoverage', 0.6); range('stoneWear', 0.65);
      if (p.variant === 'dry_ruin' && typeof p.flow === 'number' && p.flow !== 0) {
        bad('params.flow', `dry_ruin must have flow exactly 0 (got ${show(p.flow)})`);
      }
      if (typeof input.id === 'string' && typeof p.variant === 'string') {
        const seg = input.id.split('.')[2];
        if (seg !== p.variant) bad('id', `variant segment ${show(seg)} must equal params.variant ${show(p.variant)}`);
      }
    }
  }
  return out;
}

export function validateRecipe(input: unknown): FountainRecipe {
  const issues = collectIssues(input);
  if (issues.length) throw new RecipeValidationError(issues);
  return input as FountainRecipe;
}
